export const CRM_TOUR_VERSION = "2";
export const CRM_FULL_TOUR_KEY = "crm-full";
export const CRM_OVERVIEW_TOUR_KEY = "crm-section-overview";

/**
 * Selector used by the last step of every section tour. It points to the page
 * help button, so the user always learns where the tour can be replayed from.
 */
export const PAGE_HELP_TOUR_TARGET = '[data-tour="page-help"]';

/**
 * Section tours anchor on explicit `data-tour` attributes, never on layout
 * classes. The attribute is a contract between the page and the tour: a
 * refactor of the visual structure does not silently drop a step.
 */
const sectionTour = ({ key, route, copyKey, steps }) => ({
  key,
  version: CRM_TOUR_VERSION,
  route,
  copyKey,
  steps: Object.freeze([
    ...steps.map((step) =>
      Object.freeze({
        key: `${key}.${step.id}`,
        route,
        element: step.element,
        titleKey: `ncrm.tours.sections.${copyKey}.steps.${step.id}.title`,
        descriptionKey: `ncrm.tours.sections.${copyKey}.steps.${step.id}.description`,
        side: step.side || "bottom",
        align: step.align || "start",
      })
    ),
    Object.freeze({
      key: `${key}.help`,
      route,
      element: PAGE_HELP_TOUR_TARGET,
      titleKey: "ncrm.tours.help.title",
      descriptionKey: "ncrm.tours.help.description",
      side: "bottom",
      align: "end",
    }),
  ]),
});

const TOUR_DEFINITIONS = Object.freeze({
  [CRM_FULL_TOUR_KEY]: Object.freeze({
    key: CRM_FULL_TOUR_KEY,
    version: CRM_TOUR_VERSION,
    route: "/ncrm",
    copyKey: "full",
    steps: Object.freeze([
      Object.freeze({
        key: "crm-full.welcome",
        route: "/ncrm",
        titleKey: "ncrm.tours.full.welcomeTitle",
        descriptionKey: "ncrm.tours.full.welcomeDescription",
      }),
      Object.freeze({
        key: "crm-full.dashboard",
        route: "/ncrm",
        element: '[data-tour="board-header"]',
        titleKey: "ncrm.tours.full.dashboardTitle",
        descriptionKey: "ncrm.tours.full.dashboardDescription",
        side: "bottom",
        align: "start",
      }),
      Object.freeze({
        key: "crm-full.modules",
        route: "/ncrm",
        element: '[data-tour="board-modules"]',
        titleKey: "ncrm.tours.full.modulesTitle",
        descriptionKey: "ncrm.tours.full.modulesDescription",
        side: "top",
        align: "center",
      }),
      Object.freeze({
        key: "crm-full.contacts",
        route: "/ncrm/contacts",
        element: '[data-tour="contacts-header"]',
        titleKey: "ncrm.contacts.title",
        descriptionKey: "ncrm.tours.full.contactsDescription",
        side: "bottom",
        align: "start",
      }),
      Object.freeze({
        key: "crm-full.leads",
        route: "/ncrm/leads",
        element: '[data-tour="leads-header"]',
        titleKey: "ncrm.leads.home.title",
        descriptionKey: "ncrm.tours.full.leadsDescription",
        side: "bottom",
        align: "start",
      }),
      Object.freeze({
        key: "crm-full.deals",
        route: "/ncrm/deals",
        element: '[data-tour="deals-header"]',
        titleKey: "ncrm.deals.home.title",
        descriptionKey: "ncrm.tours.full.dealsDescription",
        side: "bottom",
        align: "start",
      }),
      Object.freeze({
        key: "crm-full.help",
        route: "/ncrm",
        element: PAGE_HELP_TOUR_TARGET,
        titleKey: "ncrm.tours.full.finishTitle",
        descriptionKey: "ncrm.tours.full.finishDescription",
        side: "bottom",
        align: "end",
      }),
    ]),
  }),
  [CRM_OVERVIEW_TOUR_KEY]: sectionTour({
    key: CRM_OVERVIEW_TOUR_KEY,
    route: "/ncrm",
    copyKey: "overview",
    steps: [
      { id: "intro", element: '[data-tour="board-header"]' },
      { id: "kpi", element: '[data-tour="board-kpi"]' },
      { id: "modules", element: '[data-tour="board-modules"]', side: "top", align: "center" },
      { id: "widgets", element: '[data-tour="board-widgets"]', align: "end" },
    ],
  }),
  "crm-section-activities": sectionTour({
    key: "crm-section-activities",
    route: "/ncrm/activities",
    copyKey: "activities",
    steps: [
      { id: "intro", element: '[data-tour="activities-header"]' },
      { id: "create", element: '[data-tour="activities-create"]', align: "end" },
      { id: "filters", element: '[data-tour="activities-filters"]' },
      { id: "list", element: '[data-tour="activities-list"]', side: "top", align: "center" },
    ],
  }),
  "crm-section-contacts": sectionTour({
    key: "crm-section-contacts",
    route: "/ncrm/contacts",
    copyKey: "contacts",
    steps: [
      { id: "intro", element: '[data-tour="contacts-header"]' },
      { id: "search", element: '[data-tour="contacts-search"]', align: "end" },
      { id: "advancedFilter", element: '[data-tour="contacts-advanced-filter"]', align: "end" },
      { id: "create", element: '[data-tour="contacts-create"]', align: "end" },
      { id: "views", element: '[data-tour="contacts-views"]' },
    ],
  }),
  "crm-section-leads": sectionTour({
    key: "crm-section-leads",
    route: "/ncrm/leads",
    copyKey: "leads",
    steps: [
      { id: "intro", element: '[data-tour="leads-header"]' },
      { id: "kpi", element: '[data-tour="leads-kpi"]' },
      { id: "widgets", element: '[data-tour="leads-widgets"]', side: "top", align: "center" },
      { id: "boards", element: '[data-tour="leads-boards"]', side: "top", align: "center" },
      { id: "create", element: '[data-tour="leads-create-board"]', align: "end" },
    ],
  }),
  "crm-section-deals": sectionTour({
    key: "crm-section-deals",
    route: "/ncrm/deals",
    copyKey: "deals",
    steps: [
      { id: "intro", element: '[data-tour="deals-header"]' },
      { id: "kpi", element: '[data-tour="deals-kpi"]' },
      { id: "widgets", element: '[data-tour="deals-widgets"]', side: "top", align: "center" },
      { id: "boards", element: '[data-tour="deals-boards"]', side: "top", align: "center" },
      { id: "create", element: '[data-tour="deals-create-board"]', align: "end" },
    ],
  }),
  "crm-section-contracts": sectionTour({
    key: "crm-section-contracts",
    route: "/ncrm/contracts",
    copyKey: "contracts",
    steps: [
      { id: "intro", element: '[data-tour="contracts-header"]' },
      { id: "filters", element: '[data-tour="contracts-filters"]' },
      { id: "views", element: '[data-tour="contracts-views"]' },
      { id: "list", element: '[data-tour="contracts-list"]', side: "top", align: "center" },
    ],
  }),
  "crm-section-onboarding": sectionTour({
    key: "crm-section-onboarding",
    route: "/ncrm/onboarding",
    copyKey: "onboarding",
    steps: [
      { id: "intro", element: '[data-tour="onboarding-header"]' },
      { id: "kpi", element: '[data-tour="onboarding-kpi"]' },
      { id: "boards", element: '[data-tour="onboarding-boards"]', side: "top", align: "center" },
      { id: "templates", element: '[data-tour="onboarding-templates"]', align: "end" },
    ],
  }),
  "crm-section-products": sectionTour({
    key: "crm-section-products",
    route: "/ncrm/products",
    copyKey: "products",
    steps: [
      { id: "intro", element: '[data-tour="products-header"]' },
      { id: "filters", element: '[data-tour="products-filters"]' },
      { id: "create", element: '[data-tour="products-create"]', align: "end" },
      { id: "list", element: '[data-tour="products-list"]', side: "top", align: "center" },
    ],
  }),
  "crm-section-data-integrations": sectionTour({
    key: "crm-section-data-integrations",
    route: "/ncrm/data-integrations",
    copyKey: "dataIntegrations",
    steps: [
      { id: "intro", element: '[data-tour="integrations-header"]' },
      { id: "tabs", element: '[data-tour="integrations-tabs"]' },
      { id: "connectedApps", element: '[data-tour="integrations-connected-apps"]', align: "end" },
    ],
  }),
  "crm-section-email-marketing": sectionTour({
    key: "crm-section-email-marketing",
    route: "/ncrm/email-marketing",
    copyKey: "emailMarketing",
    steps: [
      { id: "intro", element: '[data-tour="email-marketing-header"]' },
      { id: "sync", element: '[data-tour="email-marketing-sync"]', align: "end" },
      { id: "filters", element: '[data-tour="email-marketing-filters"]' },
      { id: "tabs", element: '[data-tour="email-marketing-tabs"]' },
    ],
  }),
  "crm-section-workflows": sectionTour({
    key: "crm-section-workflows",
    route: "/ncrm/workflows",
    copyKey: "workflows",
    steps: [
      { id: "intro", element: '[data-tour="workflows-header"]' },
      { id: "kpi", element: '[data-tour="workflows-kpi"]' },
      { id: "list", element: '[data-tour="workflows-list"]', side: "top", align: "center" },
      { id: "create", element: '[data-tour="workflows-create"]', align: "end" },
    ],
  }),
});

const CONTEXT_TOUR_BY_ROUTE = Object.freeze(
  Object.fromEntries(
    Object.values(TOUR_DEFINITIONS)
      .filter((tour) => tour.key !== CRM_FULL_TOUR_KEY)
      .map((tour) => [tour.route, tour.key])
  )
);

const normalizeRoutePath = (value) => {
  const path = String(value || "").trim().split(/[?#]/, 1)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
};

export const getTourDefinition = (tourKey) => TOUR_DEFINITIONS[String(tourKey || "").trim()] || null;

export const getContextTourKeyForRoute = (routePath) =>
  CONTEXT_TOUR_BY_ROUTE[normalizeRoutePath(routePath)] || null;

export const localizeTourDefinition = (tourKey, translate) => {
  const definition = getTourDefinition(tourKey);
  if (!definition || typeof translate !== "function") return null;
  return {
    ...definition,
    steps: definition.steps.map((step) => ({
      ...step,
      title: translate(step.titleKey),
      description: translate(step.descriptionKey),
    })),
  };
};

export const listTourDefinitions = () => Object.values(TOUR_DEFINITIONS);

/**
 * Every `data-tour` anchor a tour depends on, used by the static test that
 * keeps definitions and page markup in sync.
 */
export const listTourAnchors = () =>
  Array.from(
    new Set(
      listTourDefinitions()
        .flatMap((tour) => tour.steps.map((step) => step.element))
        .filter((element) => typeof element === "string" && element.startsWith("[data-tour="))
        .map((element) => element.replace(/^\[data-tour="(.+)"\]$/, "$1"))
    )
  );
